import type { Membership, Trail } from "@hash/shared";
import { doorAmountOwed, membershipState } from "@hash/shared";

// Payment badge rules (Story 8.2). On a standard trail active members are free
// (non-tappable member badge) and everyone else owes the door fee. On a
// special-fee trail (Story 7.1) everyone owes — members the member price — so
// members get the tappable paid/unpaid badge too, unless their price is zero.
// When nothing is owed at all (no kennel door fee, or a zero special price)
// there is no paid toggle: the "free" badge renders nothing and isn't tappable.
export type BadgeKind = "member" | "paid" | "unpaid" | "free";

export interface RowStatus {
  badge: BadgeKind;
  amount: number; // cents (0 for members)
  lapsed: boolean; // show the orange "lapsed" marker
  tappable: boolean; // member badge is not tappable
  // True only when a real door fee was waived (the virgins-free comp). Lets the
  // UI show a "Free" badge for a comped row while a genuinely free trail (no fee
  // to begin with) still renders nothing.
  comped: boolean;
}

export function rowStatus(opts: {
  membership: Membership | undefined;
  paid: boolean; // attendance.paid mirror of an active door payment
  doorFee: number | undefined;
  today: string;
  trail?: Pick<Trail, "specialFee" | "memberPrice" | "nonMemberPrice">;
  virgin?: boolean; // attendance.virgin — comped when the kennel toggle is on
  virginsFreeDoor?: boolean; // kennel.virginsFreeDoor
}): RowStatus {
  const state = membershipState(opts.membership, opts.today);
  const amount = doorAmountOwed(state, opts.doorFee, opts.trail, {
    virgin: opts.virgin,
    virginsFreeDoor: opts.virginsFreeDoor,
  });
  if (state === "active" && amount === 0) {
    return { badge: "member", amount: 0, lapsed: false, tappable: false, comped: false };
  }
  // Free trail: nothing to collect, so no toggle. A stale paid bit (fee
  // removed after payment) still shows as Paid so it can be reversed.
  if (amount === 0 && !opts.paid) {
    // A door fee that would otherwise be owed but was zeroed out is a comp (the
    // virgins-free waiver); a trail with no fee at all is not.
    const bare = doorAmountOwed(state, opts.doorFee, opts.trail);
    return {
      badge: "free",
      amount: 0,
      lapsed: state === "lapsed",
      tappable: false,
      comped: bare > 0,
    };
  }
  return {
    badge: opts.paid ? "paid" : "unpaid",
    amount,
    lapsed: state === "lapsed",
    tappable: true,
    comped: false,
  };
}

export function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function todayLocalISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
